/**
 * Pure helpers for sensor / fleet health KPIs. Centralized so Posture and
 * Fleet surfaces stay in lock-step and the "0 of 0 = 100%" regression
 * cannot reappear.
 */
export interface FleetHealth {
  healthy: number;
  stale: number;
  total: number;
  hasSensors: boolean;
  /** 0-100 integer. Always 0 when there are no sensors. Callers should
   *  render `value` (or `—`) instead of this when `hasSensors` is false. */
  pct: number;
  /** What to render in a StatTile value slot. */
  value: string;
  /** What to render in the hint slot. */
  hint: string;
  tone: "default" | "success" | "warning" | "danger";
}

export function computeFleetHealth(
  healthy: number | undefined | null,
  stale: number | undefined | null,
): FleetHealth {
  const h = Math.max(0, healthy ?? 0);
  const s = Math.max(0, stale ?? 0);
  const total = h + s;
  const hasSensors = total > 0;
  const pct = hasSensors ? Math.round((h / total) * 100) : 0;
  return {
    healthy: h,
    stale: s,
    total,
    hasSensors,
    pct,
    value: hasSensors ? `${pct}%` : "—",
    hint: hasSensors
      ? `${h} of ${total} sensors reporting`
      : "No sensors reporting yet",
    tone: !hasSensors
      ? "default"
      : pct >= 95
        ? "success"
        : pct >= 80
          ? "warning"
          : "danger",
  };
}
